import { useEffect, useState } from "react";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { CheckSquare, ChevronDown, ChevronUp, Copy, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import type { EdgeFunctionLog } from "@/types/edge-function-logs";
import { AgentAccessPanel, isAgentAccessDenial } from "./AgentAccessPanel";

interface EdgeFunctionLogDetailsSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  selectedLog: EdgeFunctionLog | null;
  getStatusBadge: (status: string) => React.ReactNode;
  onResolve?: (id: string) => void;
  isResolving?: boolean;
}

const formatDate = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString() : "—");

const formatJson = (value: unknown) =>
  value === null || value === undefined ? "—" : JSON.stringify(value, null, 2);

const getJsonSections = (log: EdgeFunctionLog) => [
  ...(log.error_context ? [{ title: "Error Context", value: log.error_context }] : []),
  { title: "Request Headers", value: log.request_headers },
  { title: "Request Query", value: log.request_query },
  { title: "Request Body", value: log.request_body },
  { title: "Response Body", value: log.response_body },
];

const buildLogReport = (log: EdgeFunctionLog) => {
  const lines = [
    `Edge function: ${log.function_name}`,
    `Log ID: ${log.id}`,
    `Status: ${log.status}`,
    `Response status: ${log.response_status ?? "—"}`,
    `Duration: ${log.duration_ms != null ? `${log.duration_ms} ms` : "—"}`,
    `Created: ${formatDate(log.created_at)}`,
    `Completed: ${formatDate(log.completed_at)}`,
  ];
  if (log.occurrence_count > 1) {
    lines.push(
      `Occurrences: ${log.occurrence_count} (request/response below are from the first)`,
      `Last occurred: ${formatDate(log.last_occurred_at)}`,
    );
  }
  lines.push(
    `Caller: ${log.caller_email || "—"} (${log.caller_id || "no caller id"})`,
    `Impersonated: ${log.is_impersonated ? `yes, by ${log.impersonated_by || "—"}` : "no"}`,
  );
  if (log.status === "failed") {
    lines.push(`Error: ${log.error_message || "—"}`);
    if (log.resolved_at) lines.push(`Resolved: ${formatDate(log.resolved_at)}`);
  }

  const sections = getJsonSections(log).map(({ title, value }) => `## ${title}\n${formatJson(value)}`);
  return [lines.join("\n"), ...sections].join("\n\n");
};

const JsonSection = ({
  title,
  value,
  isExpanded,
  onToggle,
}: {
  title: string;
  value: unknown;
  isExpanded: boolean;
  onToggle: () => void;
}) => {
  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(value, null, 2));
    toast.success(`${title} copied to clipboard`);
  };

  return (
    <div className="border border-genie-border rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full p-3 flex items-center justify-between hover:bg-genie-background transition-colors"
      >
        <h3 className="font-semibold text-genie-text-primary">{title}</h3>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-genie-text-secondary" />
        ) : (
          <ChevronDown className="h-4 w-4 text-genie-text-secondary" />
        )}
      </button>

      {isExpanded && (
        <div className="border-t border-genie-border bg-genie-background">
          <div className="p-3 flex justify-end">
            <Button size="sm" variant="ghost" onClick={handleCopy} className="h-7 gap-1">
              <Copy className="h-3 w-3" />
              Copy
            </Button>
          </div>
          <pre className="text-xs font-mono text-genie-text-primary bg-white border border-genie-border rounded-lg p-3 mx-3 mb-3 overflow-x-auto whitespace-pre-wrap break-all">
            {formatJson(value)}
          </pre>
        </div>
      )}
    </div>
  );
};

export const EdgeFunctionLogDetailsSidebar = ({
  isOpen,
  onClose,
  selectedLog,
  getStatusBadge,
  onResolve,
  isResolving,
}: EdgeFunctionLogDetailsSidebarProps) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  useEffect(() => {
    setExpandedSections(new Set());
  }, [selectedLog?.id]);

  if (!selectedLog) return null;

  const jsonSections = getJsonSections(selectedLog);
  const allExpanded = jsonSections.every(({ title }) => expandedSections.has(title));

  const toggleSection = (title: string) =>
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (!next.delete(title)) next.add(title);
      return next;
    });

  const toggleAll = () =>
    setExpandedSections(allExpanded ? new Set() : new Set(jsonSections.map(({ title }) => title)));

  const handleCopyReport = async () => {
    try {
      await navigator.clipboard.writeText(buildLogReport(selectedLog));
      toast.success("Log details copied to clipboard");
    } catch {
      toast.error("Couldn't copy log details");
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent
        side="right"
        showClose={false}
        className="w-full sm:max-w-xl flex flex-col h-full p-0"
      >
        <SheetTitle className="sr-only">Edge Function Log Details</SheetTitle>
        <SheetDescription className="sr-only">
          View raw request and response data for an edge function call
        </SheetDescription>
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-genie-border">
            <div>
              <h2 className="text-xl font-bold text-genie-text-primary">
                {selectedLog.function_name}
              </h2>
              <p className="text-sm text-genie-text-secondary">Log ID: {selectedLog.id}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button variant="ghost" size="sm" onClick={handleCopyReport} className="gap-1.5">
                <Copy className="h-4 w-4" />
                {selectedLog.status === "failed" ? "Copy error details" : "Copy details"}
              </Button>
              <SheetClose asChild>
                <Button variant="ghost" size="sm" title="Close details">
                  <X className="h-4 w-4" />
                </Button>
              </SheetClose>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="px-6 py-4 space-y-6">
              {/* Summary */}
              <div className="space-y-3">
                <h3 className="font-semibold text-genie-text-primary">Summary</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-genie-background rounded-lg border border-genie-border">
                    <p className="text-xs text-genie-text-secondary">Status</p>
                    <div className="mt-1">{getStatusBadge(selectedLog.status)}</div>
                  </div>
                  <div className="p-3 bg-genie-background rounded-lg border border-genie-border">
                    <p className="text-xs text-genie-text-secondary">Duration</p>
                    <p className="text-sm font-medium text-genie-text-primary">
                      {selectedLog.duration_ms != null ? `${selectedLog.duration_ms} ms` : "—"}
                    </p>
                  </div>
                  <div className="p-3 bg-genie-background rounded-lg border border-genie-border">
                    <p className="text-xs text-genie-text-secondary">Created</p>
                    <p className="text-sm font-medium text-genie-text-primary">
                      {new Date(selectedLog.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="p-3 bg-genie-background rounded-lg border border-genie-border">
                    <p className="text-xs text-genie-text-secondary">Completed</p>
                    <p className="text-sm font-medium text-genie-text-primary">
                      {selectedLog.completed_at
                        ? new Date(selectedLog.completed_at).toLocaleString()
                        : "—"}
                    </p>
                  </div>
                  {selectedLog.occurrence_count > 1 && (
                    <>
                      <div className="p-3 bg-genie-background rounded-lg border border-genie-border">
                        <p className="text-xs text-genie-text-secondary">Occurrences</p>
                        <p className="text-sm font-medium text-genie-text-primary">{selectedLog.occurrence_count}</p>
                      </div>
                      <div className="p-3 bg-genie-background rounded-lg border border-genie-border">
                        <p className="text-xs text-genie-text-secondary">Last Occurred</p>
                        <p className="text-sm font-medium text-genie-text-primary">
                          {new Date(selectedLog.last_occurred_at).toLocaleString()}
                        </p>
                      </div>
                    </>
                  )}
                </div>
                {selectedLog.occurrence_count > 1 && (
                  <p className="text-xs text-genie-text-secondary">
                    Identical failures were collapsed into this log. The request and response below are from the first
                    one.
                  </p>
                )}
              </div>

              {/* Caller Information */}
              <div className="space-y-3">
                <h3 className="font-semibold text-genie-text-primary">Caller</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-genie-background rounded-lg border border-genie-border">
                    <p className="text-xs text-genie-text-secondary">Caller Email</p>
                    <p className="text-sm font-medium text-genie-text-primary break-all">
                      <CopyableEmail email={selectedLog.caller_email} />
                    </p>
                  </div>
                  <div className="p-3 bg-genie-background rounded-lg border border-genie-border">
                    <p className="text-xs text-genie-text-secondary">Impersonated</p>
                    <p className="text-sm font-medium text-genie-text-primary">
                      {selectedLog.is_impersonated ? "Yes" : "No"}
                    </p>
                  </div>
                  <div className="p-3 bg-genie-background rounded-lg border border-genie-border flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <p className="text-xs text-genie-text-secondary">Caller ID</p>
                      <p className="text-xs font-mono text-genie-text-primary break-all">
                        {selectedLog.caller_id || "—"}
                      </p>
                    </div>
                    {selectedLog.caller_id && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          navigator.clipboard.writeText(selectedLog.caller_id!);
                          toast.success("Caller ID copied to clipboard");
                        }}
                        title="Copy caller ID to clipboard"
                        className="h-6 w-6 p-0 flex-shrink-0 mt-1"
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                  {selectedLog.is_impersonated && (
                    <div className="p-3 bg-genie-background rounded-lg border border-genie-border">
                      <p className="text-xs text-genie-text-secondary">Impersonated By</p>
                      <p className="text-xs font-mono text-genie-text-primary break-all">
                        {selectedLog.impersonated_by || "—"}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Error Details */}
              {selectedLog.status === "failed" && (
                <div className="p-3 bg-red-50 rounded-lg border border-red-200 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium text-red-900">Error</p>
                    {!selectedLog.resolved_at && onResolve && (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={isResolving}
                        onClick={() => onResolve(selectedLog.id)}
                        className="h-7 gap-1 text-genie-primary hover:text-genie-primary"
                      >
                        <CheckSquare className="h-3.5 w-3.5" />
                        Mark Resolved
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-red-800">{selectedLog.error_message || "—"}</p>
                  {selectedLog.resolved_at && (
                    <p className="text-xs text-red-800">
                      Resolved: {new Date(selectedLog.resolved_at).toLocaleString()}
                    </p>
                  )}
                </div>
              )}

              {isAgentAccessDenial(selectedLog) && <AgentAccessPanel log={selectedLog} />}

              {/* Raw request/response */}
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-semibold text-genie-text-primary">Raw Data</h3>
                  <button onClick={toggleAll} className="text-sm text-genie-primary hover:underline">
                    {allExpanded ? "Collapse all" : "Expand all"}
                  </button>
                </div>
                {jsonSections.map(({ title, value }) => (
                  <JsonSection
                    key={title}
                    title={title}
                    value={value}
                    isExpanded={expandedSections.has(title)}
                    onToggle={() => toggleSection(title)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
